import React, { useState, useEffect } from "react";
import {
  FaHome,
  FaUsers,
  FaUserPlus,
  FaCreditCard,
  FaExclamationTriangle,
  FaNewspaper,
  FaPeopleCarry,
  FaCalendarAlt,
  FaBuilding,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaHardHat,
  FaFolderOpen,
  FaClock,
  FaComments,
  FaWhatsapp,
  FaMoneyBillWave,
  FaTasks,
  FaGavel,
  FaChartBar,
  FaFileInvoiceDollar,
  FaFileSignature,
  FaBookOpen,
  FaMicrophone,
  FaCalendarCheck,
  FaPercentage,
  FaSearchPlus,
  FaUserCheck,
  FaHandshake,
  FaConciergeBell,
  FaFileContract,
  FaReceipt,
  FaHandHoldingUsd,
  FaDollarSign,
  FaCogs,
  FaTools,
  FaEye,
  FaExchangeAlt,
  FaWrench,
  FaUserFriends,
  FaIdBadge,
  FaChevronDown,
  FaChevronRight,
  FaRobot,
} from "react-icons/fa";
import { useAdminBadges } from "../hooks/useAdminBadges";
import logo from "../assets/logo.png";

// Clave para persistir estado de secciones en localStorage
const SECTIONS_STORAGE_KEY = "habitia_sidebar_admin_sections";

const SidebarAdmin = ({ selected, setSelected, collapsed = false, setCollapsed, selectedBuilding }) => {
  // Contadores de pendientes compartidos con el acceso rápido del inicio.
  const { unreadChat, pendingVerification, activeIncidents, activeMultas, whatsappNeedsHuman, pendingConsultas } =
    useAdminBadges(selectedBuilding?.buildingid);

  // Estado de secciones colapsables con persistencia en localStorage
  const [expandedSections, setExpandedSections] = useState(() => {
    try {
      const saved = localStorage.getItem(SECTIONS_STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch (e) { /* ignorar */ }
    return {
      finanzas: true,
      operaciones: false,
      residentes: false,
      personal: false,
      herramientas: false,
    };
  });

  useEffect(() => {
    try {
      localStorage.setItem(SECTIONS_STORAGE_KEY, JSON.stringify(expandedSections));
    } catch (e) { /* ignorar */ }
  }, [expandedSections]);

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Ítems que requieren un edificio seleccionado
  const requiresBuilding = new Set([
    "residentes", "gestionusuarios", "cuentasadmin", "pagosedificio", "multasadmin", "cargospuntuales", "ingresosedificio",
    "novedadesedificio", "proveedoresedificio", "cobranzaadmin", "estadocuentaedificio", "informegastos",
    "cierredemes", "incidenciasedificio", "gastosvecinosadmin",
    "asistenciaempleados", "chatadmin", "whatsappinbox", "solicitudesadmin",
    "recibosadmin", "tareasadmin", "turnosadmin",
    "reunionesadmin", "asambleas", "proyectosadmin",
    "documentacionadmin", "reservasamenities",
    "conciliacion", "mantenimiento", "onboardingresidente",
    "vercomoresidente",
  ]);

  // Ítems fijos (siempre visibles, sin grupo colapsable)
  const fixedItems = [
    { id: "homeadmin", icon: FaHome, label: "Inicio" },
    { id: "edificios", icon: FaBuilding, label: "Mis Edificios" },
    { id: "habi", icon: FaRobot, label: "Habi" },
  ];

  // Secciones colapsables del menú
  const collapsibleSections = [
    {
      key: "finanzas",
      title: "FINANZAS",
      icon: FaDollarSign,
      items: [
        // Operativos (uso diario)
        { id: "cobranzaadmin", icon: FaMoneyBillWave, label: "Cobranza", badgeKey: "cobranza" },
        { id: "pagosedificio", icon: FaCreditCard, label: "Gastos" },
        { id: "multasadmin", icon: FaGavel, label: "Multas", badgeKey: "multas" },
        { id: "cargospuntuales", icon: FaReceipt, label: "Cargos puntuales" },
        { id: "ingresosedificio", icon: FaHandHoldingUsd, label: "Ingresos del edificio" },
        { id: "gastosvecinosadmin", icon: FaPeopleCarry, label: "Gastos de vecinos", badgeKey: "gastosvecinos" },
        { id: "conciliacion", icon: FaExchangeAlt, label: "Conciliación bancaria" },
        // Reportes y cierre (lectura mensual)
        { id: "estadocuentaedificio", icon: FaChartBar, label: "Estado de cuenta" },
        { id: "informegastos", icon: FaFileInvoiceDollar, label: "Informe de gastos" },
        { id: "cierredemes", icon: FaCalendarCheck, label: "Cierre de mes" },
      ],
    },
    {
      key: "operaciones",
      title: "OPERACIONES",
      icon: FaCogs,
      items: [
        { id: "incidenciasedificio", icon: FaExclamationTriangle, label: "Incidencias", badgeKey: "incidents" },
        { id: "consultasadmin", icon: FaComments, label: "Consultas", badgeKey: "consultas" },
        { id: "whatsappinbox", icon: FaWhatsapp, label: "WhatsApp", badgeKey: "whatsapp" },
        { id: "novedadesedificio", icon: FaNewspaper, label: "Novedades" },
        { id: "proveedoresedificio", icon: FaPeopleCarry, label: "Proveedores" },
        { id: "proyectosadmin", icon: FaHardHat, label: "Obras y Proyectos" },
        { id: "mantenimiento", icon: FaWrench, label: "Mantenimientos" },
        { id: "documentacionadmin", icon: FaFolderOpen, label: "Documentos" },
      ],
    },
    {
      key: "residentes",
      title: "RESIDENTES",
      icon: FaUserFriends,
      items: [
        { id: "gestionusuarios", icon: FaUserPlus, label: "Gestión de residentes" },
        { id: "asambleas", icon: FaHandshake, label: "Asambleas" },
        { id: "reservasamenities", icon: FaConciergeBell, label: "Amenities" },
      ],
    },
    {
      key: "personal",
      title: "PERSONAL",
      icon: FaIdBadge,
      items: [
        { id: "asistenciaempleados", icon: FaClock, label: "Asistencia" },
        { id: "tareasadmin", icon: FaTasks, label: "Tareas" },
        { id: "turnosadmin", icon: FaCalendarAlt, label: "Turnos" },
        { id: "chatadmin", icon: FaComments, label: "Chat Empleados", badgeKey: "chat" },
        { id: "solicitudesadmin", icon: FaFileContract, label: "Licencias" },
        { id: "recibosadmin", icon: FaReceipt, label: "Liquidaciones" },
      ],
    },
    {
      key: "herramientas",
      title: "HERRAMIENTAS",
      icon: FaTools,
      items: [
        { id: "presupuestos", icon: FaFileSignature, label: "Presupuestos" },
        { id: "reglamento", icon: FaBookOpen, label: "Reglamento Interno" },
        { id: "actas", icon: FaFileContract, label: "Actas de Asamblea" },
        { id: "actaaudio", icon: FaMicrophone, label: "Acta desde audio" },
        { id: "vercomoresidente", icon: FaEye, label: "Ver como residente" },
      ],
    },
  ];

  const getBadge = (item) => {
    if (item.badgeKey === "chat" && unreadChat > 0) return unreadChat;
    if (item.badgeKey === "cobranza" && pendingVerification > 0) return pendingVerification;
    if (item.badgeKey === "incidents" && activeIncidents > 0) return activeIncidents;
    if (item.badgeKey === "multas" && activeMultas > 0) return activeMultas;
    if (item.badgeKey === "whatsapp" && whatsappNeedsHuman > 0) return whatsappNeedsHuman;
    if (item.badgeKey === "consultas" && pendingConsultas > 0) return pendingConsultas;
    return 0;
  };

  // Badge sumario para una sección colapsada
  const getSectionBadge = (section) => {
    let total = 0;
    section.items.forEach((item) => { total += getBadge(item); });
    return total;
  };

  const handleToggle = () => {
    if (setCollapsed) {
      setCollapsed(!collapsed);
    }
  };

  // Renderiza un ítem del menú
  const renderMenuItem = (item) => {
    const isActive = selected === item.id;
    const needsBuilding = requiresBuilding.has(item.id);
    const isDisabled = needsBuilding && !selectedBuilding;
    const badge = getBadge(item);

    return (
      <div
        key={item.id}
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        aria-current={isActive ? "page" : undefined}
        aria-disabled={isDisabled || undefined}
        style={{
          ...styles.menuItem,
          ...(isActive ? styles.menuItemActive : {}),
          ...(isDisabled ? styles.menuItemDisabled : {}),
          justifyContent: collapsed ? "center" : "flex-start",
          padding: collapsed ? "8px" : "8px 10px",
        }}
        onClick={() => {
          if (isDisabled) return;
          setSelected(item.id);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !isDisabled) setSelected(item.id);
        }}
        onFocus={(e) => {
          // Foco visible para keyboard nav (a11y).
          e.currentTarget.style.outline = "2px solid #0080ff";
          e.currentTarget.style.outlineOffset = "-2px";
        }}
        onBlur={(e) => {
          e.currentTarget.style.outline = "none";
        }}
        onMouseEnter={(e) => {
          if (!isActive && !isDisabled) {
            e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.08)";
          }
        }}
        onMouseLeave={(e) => {
          if (!isActive && !isDisabled) {
            e.currentTarget.style.backgroundColor = "transparent";
          }
        }}
        title={
          isDisabled
            ? "Requiere seleccionar un edificio"
            : collapsed
            ? item.label
            : ""
        }
      >
        <item.icon
          size={18}
          aria-hidden="true"
          color={
            isDisabled
              ? "rgba(255, 255, 255, 0.25)"
              : isActive
              ? "#0080ff"
              : "rgba(255, 255, 255, 0.7)"
          }
          style={{ flexShrink: 0 }}
        />
        {!collapsed && (
          <span style={{
            ...styles.menuLabel,
            color: isDisabled ? "rgba(255, 255, 255, 0.25)" : isActive ? "#0080ff" : "#ffffff",
            fontWeight: 400,
          }}>
            {item.label}
          </span>
        )}
        {!collapsed && needsBuilding && !isDisabled && (
          <span
            aria-hidden="true"
            title="Específico del edificio seleccionado"
            style={{
              marginLeft: "auto",
              flexShrink: 0,
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: "rgba(0, 128, 255, 0.45)",
            }}
          />
        )}
        {badge > 0 && (
          <span
            aria-label={`${badge} ${badge === 1 ? "pendiente" : "pendientes"}`}
            style={{
              ...styles.badge,
              ...(collapsed ? styles.badgeCollapsed : {}),
              ...(needsBuilding && !isDisabled && !collapsed ? { marginLeft: 6 } : {}),
            }}
          >
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </div>
    );
  };

  return (
    <aside
      role="navigation"
      aria-label="Menú principal administrador"
      data-tutorial="sidebar"
      style={{
        ...styles.sidebar,
        width: collapsed ? 60 : 220,
        minWidth: collapsed ? 60 : 220,
      }}
    >
      {/* Logo y Toggle */}
      <div style={{
        ...styles.header,
        justifyContent: collapsed ? "center" : "space-between",
        padding: collapsed ? "12px 6px" : "12px 10px",
      }}>
        {!collapsed && (
          <div
            style={styles.logoContainer}
            onClick={() => setSelected("homeadmin")}
          >
            <img
              src={logo}
              alt="Habitia"
              style={{
                ...styles.logo,
                height: 48,
                filter: "brightness(0) invert(1)",
              }}
            />
            {process.env.REACT_APP_FIREBASE_PROJECT_ID === "habitia-dev-8b512" && (
              <span style={{
                display: "block",
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.6)",
                marginTop: 2,
                lineHeight: 1,
              }}>Testing</span>
            )}
          </div>
        )}
        <button
          style={styles.toggleBtn}
          onClick={handleToggle}
          aria-label={collapsed ? "Expandir menú" : "Minimizar menú"}
          title={collapsed ? "Expandir" : "Minimizar"}
        >
          {collapsed ? (
            <FaAngleDoubleRight size={14} color="#ffffff" />
          ) : (
            <FaAngleDoubleLeft size={14} color="#ffffff" />
          )}
        </button>
      </div>

      {/* Menú */}
      <nav style={styles.nav}>
        {/* Ítems fijos (Inicio, Mis Edificios, Habi) */}
        <div style={styles.fixedGroup}>
          {fixedItems.map(renderMenuItem)}
        </div>
        {!collapsed && <div style={styles.topDivider} />}

        {/* Secciones colapsables */}
        {collapsibleSections.map((section) => {
          const isExpanded = expandedSections[section.key];
          const sectionBadge = getSectionBadge(section);
          const SectionIcon = section.icon;

          return (
            <div key={section.key} style={styles.section} data-tutorial={`sidebar-${section.key}`}>
              {/* Header de sección clickeable */}
              {!collapsed ? (
                <div
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  style={styles.sectionHeader}
                  onClick={() => toggleSection(section.key)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") toggleSection(section.key);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <SectionIcon size={12} color="rgba(255, 255, 255, 0.4)" style={{ flexShrink: 0 }} />
                  <span style={styles.sectionTitle}>{section.title}</span>
                  <div style={styles.sectionHeaderRight}>
                    {!isExpanded && sectionBadge > 0 && (
                      <span style={styles.sectionBadge}>
                        {sectionBadge > 99 ? "99+" : sectionBadge}
                      </span>
                    )}
                    {isExpanded ? (
                      <FaChevronDown size={10} color="rgba(255, 255, 255, 0.3)" />
                    ) : (
                      <FaChevronRight size={10} color="rgba(255, 255, 255, 0.3)" />
                    )}
                  </div>
                </div>
              ) : (
                <div style={styles.sectionDivider} />
              )}

              {/* Ítems de la sección (visible si expandida o si sidebar colapsado) */}
              {(isExpanded || collapsed) && (
                <div style={!collapsed ? styles.sectionItems : undefined}>
                  {section.items.map(renderMenuItem)}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
};

const styles = {
  sidebar: {
    position: "fixed",
    left: 0,
    top: 0,
    bottom: 0,
    backgroundColor: "#1a1a1a",
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    minHeight: "100vh",
    flexShrink: 0,
    transition: "width 0.2s ease, min-width 0.2s ease",
    overflow: "hidden",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    zIndex: 100,
  },
  header: {
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
  },
  logoContainer: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    flex: 1,
  },
  logo: {
    width: "auto",
    objectFit: "contain",
  },
  toggleBtn: {
    width: 32,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    transition: "all 0.15s ease",
    flexShrink: 0,
  },
  nav: {
    flex: 1,
    overflowY: "auto",
    padding: "8px 6px",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minHeight: 0,
  },
  fixedGroup: {
    marginBottom: 2,
  },
  topDivider: {
    height: 1,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    margin: "4px 10px 8px",
  },
  section: {
    marginBottom: 10,
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: "5px 10px 5px",
    cursor: "pointer",
    borderRadius: 6,
    transition: "background-color 0.15s ease",
    userSelect: "none",
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 400,
    color: "rgba(255, 255, 255, 0.5)",
    letterSpacing: "1px",
    textTransform: "uppercase",
    flex: 1,
  },
  sectionHeaderRight: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  sectionBadge: {
    backgroundColor: "#ef4444",
    color: "#ffffff",
    fontSize: 9,
    fontWeight: 400,
    borderRadius: 10,
    padding: "0px 5px",
    minWidth: 14,
    textAlign: "center",
    lineHeight: "14px",
  },
  sectionItems: {
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    marginLeft: 8,
    paddingLeft: 6,
    marginTop: 2,
    borderLeft: "1px solid rgba(255, 255, 255, 0.12)",
  },
  sectionDivider: {
    height: 1,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    margin: "4px 10px",
  },
  menuItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 12px",
    borderRadius: 8,
    cursor: "pointer",
    transition: "all 0.15s ease",
    position: "relative",
  },
  menuItemActive: {
    backgroundColor: "#e8f4ff",
  },
  menuItemDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  menuLabel: {
    fontSize: 14,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flex: 1,
  },
  badge: {
    backgroundColor: "#ef4444",
    color: "#ffffff",
    fontSize: 10,
    fontWeight: 400,
    borderRadius: 10,
    padding: "1px 6px",
    minWidth: 18,
    textAlign: "center",
    lineHeight: "16px",
    flexShrink: 0,
  },
  badgeCollapsed: {
    position: "absolute",
    top: 2,
    right: 2,
    fontSize: 9,
    padding: "0px 4px",
    minWidth: 14,
    lineHeight: "14px",
  },
};

export default SidebarAdmin;
